import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Mail, Loader2, Plug, Send, Calendar, CheckCircle2, Unplug, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "../lib/api";
import { PasswordInput } from "../components/PasswordInput";
import { FloatingShapes } from "../components/FloatingShapes";

const inputCls = "w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm outline-none transition-colors focus:border-primary";
const EMPTY = { smtp_host: "", smtp_port: 587, smtp_security: "starttls", smtp_user: "", smtp_password: "", from_email: "", from_name: "", firma: "" };
const PRESETS = [
  ["Gmail / Google Workspace", { smtp_host: "smtp.gmail.com", smtp_port: 587, smtp_security: "starttls" }],
  ["Outlook / Microsoft 365", { smtp_host: "smtp.office365.com", smtp_port: 587, smtp_security: "starttls" }],
  ["Aruba", { smtp_host: "smtps.aruba.it", smtp_port: 465, smtp_security: "ssl" }],
  ["Libero", { smtp_host: "smtp.libero.it", smtp_port: 465, smtp_security: "ssl" }],
];

export default function Impostazioni() {
  const [form, setForm] = useState(EMPTY);
  const [saved, setSaved] = useState(null);
  const [busy, setBusy] = useState("");
  const [params, setParams] = useSearchParams();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/mail/settings");
      setSaved(data);
      if (data.configurato) setForm((f) => ({ ...f, ...Object.fromEntries(Object.keys(EMPTY).filter((k) => k !== "smtp_password" && data[k] != null).map((k) => [k, data[k]])) }));
    } catch (e) { toast.error(errorMessage(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const g = params.get("google");
    if (!g) return;
    if (g === "ok") toast.success("Google Calendar collegato");
    else toast.error("Collegamento Google Calendar non riuscito", { description: g });
    params.delete("google");
    setParams(params, { replace: true });
  }, [params, setParams]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setBusy("save");
    try {
      const body = { ...form, smtp_port: Number(form.smtp_port), smtp_password: form.smtp_password || null };
      const { data } = await api.put("/mail/settings", body);
      setSaved((s) => ({ ...s, ...data }));
      setForm((f) => ({ ...f, smtp_password: "" }));
      toast.success("Impostazioni posta salvate");
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(""); }
  };

  const test = async (invia) => {
    setBusy(invia ? "send" : "test");
    try {
      const { data } = await api.post("/mail/settings/test", { invia_prova: invia });
      toast.success(invia ? `Email di prova inviata a ${data.inviata_a}` : "Connessione al server riuscita");
      load();
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(""); }
  };

  const remove = async () => {
    await api.delete("/mail/settings");
    setForm(EMPTY);
    load();
    toast.success("Configurazione posta rimossa");
  };

  const google = async (connect) => {
    setBusy("google");
    try {
      if (connect) {
        const { data } = await api.get("/google/calendar/connect");
        window.location.href = data.url;
        return;
      }
      await api.post("/google/calendar/disconnect");
      toast.success("Google Calendar scollegato");
      load();
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(""); }
  };

  return (
    <div data-testid="settings-page" className="relative space-y-8">
      <FloatingShapes />
      <div className="fade-up relative z-10">
        <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Account</p>
        <h1 className="mt-2 text-4xl font-bold tracking-tight sm:text-5xl">Impostazioni</h1>
        <p className="mt-2 text-sm text-muted-foreground">Collega la tua casella di posta: da qui partiranno le email di follow-up e gli inviti ai clienti.</p>
      </div>

      <div className="relative z-10 grid gap-8 lg:grid-cols-5">
        <form onSubmit={save} className="surface fade-up space-y-4 p-6 lg:col-span-3">
          <div className="flex items-center justify-between">
            <h2 className="flex items-center gap-2 text-base font-semibold md:text-lg"><Mail className="h-5 w-5 text-primary" /> Server di posta in uscita (SMTP)</h2>
            {saved?.configurato && <span data-testid="smtp-status" className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-mono ${saved.ultimo_test?.ok ? "bg-emerald-500/15 text-emerald-300" : "bg-secondary text-muted-foreground"}`}><CheckCircle2 className="h-3 w-3" />{saved.ultimo_test?.ok ? "verificato" : "salvato"}</span>}
          </div>
          <div className="flex flex-wrap gap-2">
            {PRESETS.map(([label, p]) => <button key={label} type="button" data-testid={`smtp-preset-${p.smtp_host}`} onClick={() => setForm((f) => ({ ...f, ...p }))} className="rounded-full border border-border px-3 py-1 text-xs text-muted-foreground transition-colors hover:border-primary hover:text-white">{label}</button>)}
          </div>
          <div className="grid gap-3 sm:grid-cols-6">
            <label className="text-xs text-muted-foreground sm:col-span-3">Server SMTP
              <input data-testid="smtp-host" className={`${inputCls} mt-1`} value={form.smtp_host} onChange={set("smtp_host")} placeholder="smtp.tuodominio.it" required />
            </label>
            <label className="text-xs text-muted-foreground sm:col-span-1">Porta
              <input data-testid="smtp-port" type="number" className={`${inputCls} mt-1`} value={form.smtp_port} onChange={set("smtp_port")} required />
            </label>
            <label className="text-xs text-muted-foreground sm:col-span-2">Sicurezza
              <select data-testid="smtp-security" className={`${inputCls} mt-1`} value={form.smtp_security} onChange={set("smtp_security")}>
                <option value="starttls">STARTTLS (587)</option><option value="ssl">SSL/TLS (465)</option><option value="none">Nessuna (25)</option>
              </select>
            </label>
            <label className="text-xs text-muted-foreground sm:col-span-3">Utente
              <input data-testid="smtp-user" className={`${inputCls} mt-1`} value={form.smtp_user} onChange={set("smtp_user")} placeholder="nome@tuodominio.it" autoComplete="off" />
            </label>
            <div className="text-xs text-muted-foreground sm:col-span-3">Password {saved?.has_password && <span className="text-[10px]">(salvata · lascia vuoto per non cambiarla)</span>}
              <div className="mt-1"><PasswordInput testId="smtp-password" value={form.smtp_password} onChange={set("smtp_password")} placeholder={saved?.has_password ? "••••••••" : "Password o app password"} autoComplete="new-password" className="py-2.5" /></div>
            </div>
            <label className="text-xs text-muted-foreground sm:col-span-3">Email mittente
              <input data-testid="smtp-from-email" type="email" className={`${inputCls} mt-1`} value={form.from_email} onChange={set("from_email")} placeholder="nome@tuodominio.it" required />
            </label>
            <label className="text-xs text-muted-foreground sm:col-span-3">Nome mittente
              <input data-testid="smtp-from-name" className={`${inputCls} mt-1`} value={form.from_name || ""} onChange={set("from_name")} placeholder="Mario Rossi · Azienda" />
            </label>
            <label className="text-xs text-muted-foreground sm:col-span-6">Firma (aggiunta in fondo alle email)
              <textarea data-testid="smtp-firma" rows={3} className={`${inputCls} mt-1 resize-y`} value={form.firma || ""} onChange={set("firma")} placeholder={"Mario Rossi\nResponsabile commerciale · +39 ..."} />
            </label>
          </div>
          <p className="text-[11px] text-muted-foreground">Gmail e Outlook richiedono una <b>password per le app</b> (non quella dell'account). La password viene salvata cifrata e non è mai mostrata.</p>
          <div className="flex flex-wrap gap-2 pt-1">
            <button data-testid="smtp-save" type="submit" disabled={!!busy} className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-white transition-all hover:bg-[#1D4ED8] disabled:opacity-50">{busy === "save" ? <Loader2 className="h-4 w-4 spin-slow" /> : <CheckCircle2 className="h-4 w-4" />} Salva</button>
            <button data-testid="smtp-test" type="button" disabled={!!busy || !saved?.configurato} onClick={() => test(false)} className="inline-flex items-center gap-2 rounded-full border border-border px-5 py-2.5 text-sm hover:border-primary disabled:opacity-50">{busy === "test" ? <Loader2 className="h-4 w-4 spin-slow" /> : <Plug className="h-4 w-4" />} Testa connessione</button>
            <button data-testid="smtp-send-test" type="button" disabled={!!busy || !saved?.configurato} onClick={() => test(true)} className="inline-flex items-center gap-2 rounded-full border border-border px-5 py-2.5 text-sm hover:border-primary disabled:opacity-50">{busy === "send" ? <Loader2 className="h-4 w-4 spin-slow" /> : <Send className="h-4 w-4" />} Inviami una prova</button>
            {saved?.configurato && <button data-testid="smtp-delete" type="button" onClick={remove} className="ml-auto inline-flex items-center gap-2 rounded-full px-3 py-2.5 text-xs text-muted-foreground hover:text-red-300"><Trash2 className="h-3.5 w-3.5" /> Rimuovi</button>}
          </div>
          {saved?.ultimo_test && !saved.ultimo_test.ok && <p data-testid="smtp-last-error" className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-xs text-red-300">Ultimo test fallito: {saved.ultimo_test.errore}</p>}
        </form>

        <section className="surface fade-up space-y-4 p-6 lg:col-span-2">
          <h2 className="flex items-center gap-2 text-base font-semibold md:text-lg"><Calendar className="h-5 w-5 text-[#FCD990]" /> Google Calendar</h2>
          <p className="text-sm text-muted-foreground">Gli appuntamenti confermati dopo una chiamata vengono inseriti direttamente nel tuo calendario, oltre all'invito .ics inviato via email.</p>
          {saved?.google_calendar ? (
            <div data-testid="gcal-connected" className="space-y-3">
              <p className="inline-flex items-center gap-2 rounded-full bg-emerald-500/15 px-3 py-1 text-xs font-semibold text-emerald-300"><CheckCircle2 className="h-3.5 w-3.5" /> Collegato</p>
              <button data-testid="gcal-disconnect" onClick={() => google(false)} disabled={!!busy} className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm hover:border-red-400 hover:text-red-300"><Unplug className="h-4 w-4" /> Scollega</button>
            </div>
          ) : saved?.google_configurato ? (
            <button data-testid="gcal-connect" onClick={() => google(true)} disabled={!!busy} className="inline-flex items-center gap-2 rounded-full bg-[#B8860B] px-5 py-2.5 text-sm font-semibold text-white transition-all hover:-translate-y-0.5">{busy === "google" ? <Loader2 className="h-4 w-4 spin-slow" /> : <Plug className="h-4 w-4" />} Collega Google Calendar</button>
          ) : (
            <p data-testid="gcal-not-configured" className="rounded-lg border border-border bg-background/60 p-3 text-xs text-muted-foreground">Non ancora attivo: l'amministratore deve configurare le credenziali Google (Client ID e Secret) sul server. Nel frattempo gli inviti arrivano come allegato .ics.</p>
          )}
        </section>
      </div>
    </div>
  );
}
